import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Catches /framework-download/verify?token=...
 * Calls the backend verify endpoint and routes to the success page on success.
 */
const FrameworkVerifyPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState("verifying"); // verifying | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setError("Missing verification token.");
      return;
    }
    fetch(`${BACKEND_URL}/api/framework-download/verify?token=${encodeURIComponent(token)}`, {
      method: "GET",
      redirect: "manual",
    })
      .then(async (res) => {
        if (res.type === "opaqueredirect" || (res.status >= 300 && res.status < 400)) {
          // The backend redirects to /framework-download/success?download=...
          // Browser-based fetch can't follow the redirect for us reliably, so call again with default redirect.
          const followed = await fetch(`${BACKEND_URL}/api/framework-download/verify?token=${encodeURIComponent(token)}`);
          const url = new URL(followed.url);
          const dl = url.searchParams.get("download");
          if (dl) {
            navigate(`/framework-download/success?download=${encodeURIComponent(dl)}`, { replace: true });
            return;
          }
          throw new Error("Verification did not return a download token.");
        }
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || "Verification failed.");
        }
        const data = await res.json().catch(() => null);
        if (data?.download_token) {
          navigate(`/framework-download/success?download=${encodeURIComponent(data.download_token)}`, { replace: true });
        } else {
          throw new Error("Verification returned no token.");
        }
      })
      .catch((e) => {
        setState("error");
        setError(e.message || "Could not verify the link.");
      });
  }, [token, navigate]);

  return (
    <PublicLayout>
      <section className="py-24 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 max-w-xl text-center">
          {state === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 text-[#FDB813] animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">Verifying your email…</h1>
              <p className="text-gray-600">Hang tight, this only takes a moment.</p>
            </>
          )}
          {state === "error" && (
            <>
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">We couldn't verify that link</h1>
              <p className="text-gray-600 mb-8">{error}</p>
              <Button onClick={() => navigate("/resources")} className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-3 rounded-xl">
                Request a new download <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default FrameworkVerifyPage;
